import {
    addHyperlinkToFirstColumn,
    Cell,
    TableData,
    filterRows,
    applyConditionalFormatting,
} from 'utils/data'
import { NddHierarchyMetrics, LocationMetric } from 'types/ndd'
import { translateHeadings } from 'locales/utils'
import { isGroup } from 'utils/location'
import { GeneralizedComponentPayloadType } from 'components/generalized-component/generalized-component'

export function formatWithCommas(num: number | string): string {
    return new Intl.NumberFormat('en-US').format(Number(num))
}

export function createTranslatedNddTable(
    t: (key: string) => string,
    headings: Record<string, string>,
): TableData {
    const headingKeys = [
        headings.location,
        headings.containersToSort,
        headings.infPercent,
        headings.sortedOnTime,
        headings.handoffAccuracy,
    ]
    const tableData: TableData = {
        headings: headingKeys.map((value) => ({ value })),
        rows: [],
    }
    return translateHeadings(headingKeys, t, tableData)
}

export function gatherNddLocationRows(
    locations: {
        parent_locations?: LocationMetric[] | null
        current_location: LocationMetric | null
        children_locations?: LocationMetric[] | null
        grand_children_locations?: LocationMetric[] | null
    },
    makeRow: (
        loc: LocationMetric,
        src: 'current' | 'parent' | 'child' | 'grandchild',
    ) => Cell[],
): Cell[][] {
    const rows: Cell[][] = []
    locations.parent_locations?.forEach((loc) =>
        rows.push(makeRow(loc, 'parent')),
    )
    if (locations.current_location) {
        rows.push(makeRow(locations.current_location, 'current'))
    }
    locations.children_locations?.forEach((loc) =>
        rows.push(makeRow(loc, 'child')),
    )
    locations.grand_children_locations?.forEach((loc) =>
        rows.push(makeRow(loc, 'grandchild')),
    )
    return rows
}

export function buildNddPayloadFromTable(
    table: TableData,
    currentLocation: string,
    t: (key: string) => string,
    toggles: Record<string, string>,
): GeneralizedComponentPayloadType {
    if (isGroup(currentLocation)) {
        return {
            [t(toggles.showDistricts)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        group: filterRows(table, (row) => {
                            const childOf = row[0]?.childOf
                            // Show parent, current, and child (districts) - hide grandchild (stores)
                            return childOf !== 'grandchild'
                        }),
                    },
                },
            ],
            [t(toggles.showStores)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        group: filterRows(table, (row) => {
                            const childOf = row[0]?.childOf
                            // Show parent, current, and grandchild (stores) - hide child (districts)
                            return childOf !== 'child'
                        }),
                    },
                },
            ],
        }
    }
    return {
        [t(toggles.viewInPercent || 'filters.viewInPercent')]: [
            {
                title: '',
                subtitle: '',
                data: { nonGroup: table },
            },
        ],
    }
}

export function pinAndSortNddRows(
    rows: Cell[][],
    sortFn: (a: Cell[], b: Cell[]) => number,
): Cell[][] {
    const pinned: Cell[][] = []
    const sortable: Cell[][] = []

    rows.forEach((row) => {
        const source = row[0]?.childOf
        if (source === 'current' || source === 'parent') pinned.push(row)
        else sortable.push(row)
    })

    sortable.sort(sortFn)
    return [...pinned, ...sortable]
}

export function buildNddRgdPayload(
    data: NddHierarchyMetrics[] | NddHierarchyMetrics,
    deps: {
        headings: Record<string, string>
        currentLocation: string
        t: (key: string) => string
        toggles: Record<string, string>
    },
): GeneralizedComponentPayloadType {
    const { headings, currentLocation, t, toggles } = deps

    // Handle array response - take the first item
    const hierarchyData = Array.isArray(data) ? data[0] : data

    if (!hierarchyData) {
        return {
            [t(toggles.viewInPercent || 'filters.viewInPercent')]: [
                {
                    title: '',
                    subtitle: '',
                    data: { empty: { headings: [], rows: [] } },
                },
            ],
        }
    }

    const table = createTranslatedNddTable(t, headings)

    const makeRow = (
        loc: LocationMetric,
        src: 'current' | 'parent' | 'child' | 'grandchild',
    ) => [
        {
            value: loc?.location_description ?? loc?.location_id ?? '',
            childOf: src,
            locationId: loc?.location_id, // Store location_id for filtering
        },
        { value: formatWithCommas(loc?.containers_to_be_sorted ?? 0) },
        applyConditionalFormatting(
            `${(loc?.inf_percentage ?? 0).toFixed(1)}%`,
            loc?.inf_percentage_indicator ? 'hc-clr-success' : 'hc-clr-error',
        ),
        applyConditionalFormatting(
            `${(loc?.sorted_on_time_percentage ?? 0).toFixed(1)}%`,
            loc?.is_on_time ? 'hc-clr-success' : 'hc-clr-error',
        ),
        applyConditionalFormatting(
            `${(loc?.hand_off_accuracy_percentage ?? 0).toFixed(1)}%`,
            loc?.is_accurate ? 'hc-clr-success' : 'hc-clr-error',
        ),
    ]

    table.rows = gatherNddLocationRows(hierarchyData, makeRow)
    table.rows = pinAndSortNddRows(table.rows, (a, b) => {
        const getLocationId = (cell: Cell) => {
            // Use locationId if available, otherwise fall back to extracting from value
            if (cell.locationId) {
                return typeof cell.locationId === 'string'
                    ? parseInt(cell.locationId.match(/\d+/)?.[0] ?? '0', 10)
                    : Number(cell.locationId)
            }
            return parseInt(cell.value.match(/\d+/)?.[0] ?? '0', 10)
        }
        return getLocationId(a[0]) - getLocationId(b[0])
    })

    const tableWithLinks = addHyperlinkToFirstColumn(table, currentLocation)

    // Remove hyperlink from the "Target" row when already at company (TARGET) level
    if (hierarchyData.location_type === 'TARGET' && tableWithLinks?.rows) {
        tableWithLinks.rows = tableWithLinks.rows.map((row) => {
            if (row?.[0]?.value === 'Target') {
                delete row[0].url
            }
            return row
        })
    }

    return buildNddPayloadFromTable(tableWithLinks, currentLocation, t, toggles)
}
